import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  getInfiniteValidQualificationDate,
  isInfiniteValidQualificationDate,
} from '@wilson/qualification-v2/common';
import { differenceInCalendarDays, isAfter, isBefore, isDate } from 'date-fns';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { distinctUntilChanged, merge, Subject, takeUntil, tap } from 'rxjs';
import { QualificationNamePipe } from '@wilson/qualification-v2/common';
import { NewQualificationFromTrainings } from '../training-qualifications-finalize-drawer.service';

@Component({
  selector:
    'wilson-training-qualifications-finalize-new-qualifications-section',
  imports: [
    CommonModule,
    FormsModule,
    NzCollapseModule,
    NzDatePickerModule,
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzSwitchModule,
    ReactiveFormsModule,
    TranslatePipe,
    NzTagModule,
    QualificationNamePipe,
  ],
  templateUrl:
    './training-qualifications-finalize-new-qualifications-section.component.html',
  styleUrl:
    './training-qualifications-finalize-new-qualifications-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrainingQualificationsFinalizeNewQualificationsSectionComponent
  implements OnInit, OnDestroy
{
  private destroyed$ = new Subject<void>();
  private destroyValidUntilListener$ = new Subject<void>();
  private destroyInfiniteValidListener$ = new Subject<void>();
  private _validUntilFormControl: FormControl<Date | null> =
    new FormControl<Date | null>(null, [Validators.required]);
  private _infiniteValidFormControl: FormControl<boolean | null> =
    new FormControl<boolean>(false);

  private _newQualificationFromTrainings: NewQualificationFromTrainings[] = [];
  @Input() set newQualificationFromTrainings(
    newQualifications: NewQualificationFromTrainings[],
  ) {
    const seen = new Set<string>();
    this._newQualificationFromTrainings = newQualifications.filter(
      (qualification) => {
        const duplicate = seen.has(qualification.qualificationId);
        seen.add(qualification.qualificationId);
        return !duplicate;
      },
    );
  }
  get newQualificationFromTrainings(): NewQualificationFromTrainings[] {
    return this._newQualificationFromTrainings;
  }
  @Input() active: boolean | null = true;
  @Input() disabled: boolean | null = false;
  @Input() acquiredAtFormControl: FormControl<Date | null> =
    new FormControl<Date | null>(null, [Validators.required]);
  @Input() validFromFormControl: FormControl<Date | null> =
    new FormControl<Date | null>(null, [Validators.required]);
  @Input() set validUntilFormControl(
    newValidUntilFormControl: FormControl<Date | null>,
  ) {
    this.destroyValidUntilListener$.next();
    this._validUntilFormControl = newValidUntilFormControl;
    this.setupValidUntilListener();
  }
  get validUntilFormControl(): FormControl<Date | null> {
    return this._validUntilFormControl;
  }
  @Input() set infiniteValidFormControl(
    newInfiniteValidFormControl: FormControl<boolean | null>,
  ) {
    this.destroyInfiniteValidListener$.next();
    this._infiniteValidFormControl = newInfiniteValidFormControl;
    this.setupInfiniteValidListener();
  }
  get infiniteValidFormControl(): FormControl<boolean | null> {
    return this._infiniteValidFormControl;
  }
  @Input() confirmedByEmployeeFormControl: FormControl<boolean | null> =
    new FormControl<boolean>(false);

  @Input() disabledAcquiredAtDates: (current: Date) => boolean = (
    current: Date,
  ): boolean => {
    const validFromDate = this.validFromFormControl.value;
    let dateIsDisabled = false;
    if (validFromDate && differenceInCalendarDays(current, validFromDate)) {
      dateIsDisabled = isAfter(current, validFromDate);
    }
    return dateIsDisabled;
  };
  @Input() disabledValidFromDates: (current: Date) => boolean = (
    current: Date,
  ): boolean => {
    const acquiredAtDate = this.acquiredAtFormControl.value;
    const validUntilDate = this.validUntilFormControl.value;
    let dateIsDisabled = false;
    if (
      acquiredAtDate &&
      validUntilDate &&
      differenceInCalendarDays(current, acquiredAtDate) &&
      differenceInCalendarDays(current, validUntilDate)
    ) {
      dateIsDisabled =
        isBefore(current, acquiredAtDate) || isAfter(current, validUntilDate);
    } else if (
      acquiredAtDate &&
      differenceInCalendarDays(current, acquiredAtDate)
    ) {
      dateIsDisabled = isBefore(current, acquiredAtDate);
    } else if (
      validUntilDate &&
      differenceInCalendarDays(current, validUntilDate)
    ) {
      dateIsDisabled = isAfter(current, validUntilDate);
    }
    return dateIsDisabled;
  };
  @Input() disabledValidUntilDates: (current: Date) => boolean = (
    current: Date,
  ): boolean => {
    const validFromDate = this.validFromFormControl.value;
    let dateIsDisabled = false;
    if (validFromDate && differenceInCalendarDays(current, validFromDate)) {
      dateIsDisabled = isBefore(current, validFromDate);
    }
    return dateIsDisabled;
  };

  protected Validators = Validators;

  ngOnInit(): void {
    this.destroyValidUntilListener$.next();
    this.setupValidUntilListener();
    this.destroyInfiniteValidListener$.next();
    this.setupInfiniteValidListener();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.destroyValidUntilListener$.next();
    this.destroyValidUntilListener$.complete();
    this.destroyInfiniteValidListener$.next();
    this.destroyInfiniteValidListener$.complete();
  }

  private setupValidUntilListener(): void {
    this.validUntilFormControl.valueChanges
      .pipe(
        takeUntil(merge(this.destroyed$, this.destroyValidUntilListener$)),
        distinctUntilChanged(),
        tap((newValue) => {
          const newIsInfiniteValidDate = !!(
            newValue &&
            isDate(newValue) &&
            isInfiniteValidQualificationDate(newValue)
          );
          if (this.infiniteValidFormControl.value !== newIsInfiniteValidDate) {
            this.infiniteValidFormControl.patchValue(newIsInfiniteValidDate, {
              emitEvent: false,
            });
          }
        }),
      )
      .subscribe();
  }

  private setupInfiniteValidListener(): void {
    this.infiniteValidFormControl.valueChanges
      .pipe(
        takeUntil(merge(this.destroyed$, this.destroyInfiniteValidListener$)),
        distinctUntilChanged(),
        tap((newValue) => {
          const newValidUntilDate = newValue
            ? getInfiniteValidQualificationDate()
            : null;
          if (this.validUntilFormControl.value !== newValidUntilDate) {
            this.validUntilFormControl.patchValue(newValidUntilDate, {
              emitEvent: false,
            });
          }
        }),
      )
      .subscribe();
  }
}
